import * as R from "ramda";
import * as React from "react";
import { FaCheck, FaCloudUploadAlt, FaGraduationCap, FaPlayCircle, FaThLarge, FaUndo } from "react-icons/fa";
import { Link } from "react-router-dom";
import styled from "styled-components";

import fonts from "../../../../styles/fonts";
import { courseComponentViews, courseUnitViews, fullCourseViews } from "../../../../utils/courseViews";
import { PreferredLocale } from "../../../../utils/locales";
import { AssessmentLink } from "../../../course-author/navigation/AssessmentLink";
import {
  CourseContentType,
  CourseUnitView,
  DefaultNames,
  ICourseComponent,
  ICourseEditOperations,
  ICourseNavigationOperations,
  ICourseUnit,
  IDisplayGroups,
  IFullCourse,
  Slide,
} from "../../../course-author/types";
import { ClickableMenuItem } from "../../../utils/ClickableElement";
import { NavLink } from "../../../utils/SideMenu/SideMenu.component";
import { LinkWrapper } from "../../../utils/SideMenu/SideMenu.styles";
import ToggleDisplay from "../../../utils/ToggleDisplay";
import { ClickableComponentLink, ComponentLinkDiv } from "./ComponentLink";
import InstructionGroupsList from "./InstructionGroupList";
import { UnitPathLinks } from "./UnitPathLinks";
import UnitsList from "./UnitsList";

const CourseAuthorLinkWrapper = styled(LinkWrapper)`
  height: auto;
  min-height: 3rem;
  svg {
    height: 1.8rem;
  }
`;
const ScrollableNav = styled.nav`
  overflow-y: auto;
  font-family: ${fonts.OpenSans};
  max-height: calc(100vh - 17.5rem);
  margin-bottom: auto;
  width: 100%;
`;

const PublishDiv = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  button {
    width: auto;
  }
`;

const NonClickableElement = styled.div`
  background: inherit;
  border: none;
  color: inherit;
  cursor: default;
  display: flex;
  font-family: inherit;
  font-size: inherit;
  outline: none;
  padding: 0;
`;

const UnpublishButton = styled(ClickableMenuItem)`
  transition: color 0.25s ease;
  &:hover {
    color: #aaa;
  }
`;

export interface ICourseNavigationProps {
  course: IFullCourse;
  currentLocale: PreferredLocale;
  displayedComponent: Slide | null;
  editOperations: ICourseEditOperations;
  navigationOperations: ICourseNavigationOperations;
  previewCourseInst: () => void;
  publicationVersion?: number;
  rootUrl: string;
  unitPath: CourseUnitView[];
  units: ICourseUnit[];
}

const CourseNavigation: React.FC<ICourseNavigationProps> = props => {
  const {
    course,
    currentLocale,
    displayedComponent,
    editOperations,
    navigationOperations,
    previewCourseInst,
    publicationVersion,
    rootUrl,
    unitPath,
    units,
  } = props;
  const courseTitle = fullCourseViews.localizedTitle(course, currentLocale);
  const currentUnitView = R.last(unitPath);
  const currentUnit = (currentUnitView && currentUnitView(course)) || ({} as ICourseUnit);

  const displayGroups: IDisplayGroups = R.groupBy<ICourseComponent>(
    unit => courseComponentViews.tag(unit),
    courseUnitViews.components(currentUnit) || units
  ) as any;

  const clearUnitPath = () => {
    navigationOperations.setDisplayedComponent(null)();
    navigationOperations.truncatePath(-1);
  };

  const moveUpUnitPath = (index: number) => () => {
    const view = unitPath[index];
    navigationOperations.setDisplayedComponent(view(course))();
    navigationOperations.truncatePath(index);
  };

  const isPublished = !R.isNil(publicationVersion);

  const publishCurrentVersion = () => editOperations.publishVersion(publicationVersion);
  const unpublishCurrentVersion = () => editOperations.unpublishVersion(publicationVersion);

  const shouldMigrateContent = CourseContentType.MarkdownContent === course.state.contentType;

  const previewCourseInstWithoutDefault = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    (e.target as HTMLButtonElement).blur();
    previewCourseInst();
  };

  return (
    <ScrollableNav>
      <NavLink to="/dashboard" icon={<FaThLarge />} desc="Dashboard" />
      <a>
        <ToggleDisplay shouldDisplay={!isPublished}>
          <CourseAuthorLinkWrapper>
            <ClickableMenuItem onClick={publishCurrentVersion}>
              <FaCloudUploadAlt />
              Publish
            </ClickableMenuItem>
          </CourseAuthorLinkWrapper>
        </ToggleDisplay>
        <ToggleDisplay shouldDisplay={isPublished} style={{ width: "100%" }}>
          <CourseAuthorLinkWrapper>
            <PublishDiv>
              <NonClickableElement>
                <FaCheck />
                Published
              </NonClickableElement>
              <UnpublishButton onClick={unpublishCurrentVersion}>
                <FaUndo />
              </UnpublishButton>
            </PublishDiv>
          </CourseAuthorLinkWrapper>
        </ToggleDisplay>
      </a>
      <a>
        <CourseAuthorLinkWrapper>
          <ClickableMenuItem onClick={previewCourseInstWithoutDefault}>
            <FaPlayCircle />
            Preview
          </ClickableMenuItem>
        </CourseAuthorLinkWrapper>
      </a>
      <ComponentLinkDiv data-selected={displayedComponent === null}>
        <Link to={`${rootUrl}`} onClick={clearUnitPath}>
          <CourseAuthorLinkWrapper>
            <ClickableComponentLink>
              <div style={{ display: "block" }}>
                <FaGraduationCap />
              </div>
              {courseTitle || DefaultNames.Course}
            </ClickableComponentLink>
          </CourseAuthorLinkWrapper>
        </Link>
      </ComponentLinkDiv>
      <ToggleDisplay shouldDisplay={!shouldMigrateContent}>
        <UnitPathLinks
          course={course}
          currentLocale={currentLocale}
          displayedComponent={displayedComponent}
          moveUpUnitPath={moveUpUnitPath}
          rootUrl={rootUrl}
          unitPath={unitPath}
        />
        <AssessmentLink
          course={course}
          displayedComponent={displayedComponent}
          navigationOperations={navigationOperations}
          rootUrl={rootUrl}
          unitPath={unitPath}
        />
        <InstructionGroupsList
          course={course}
          currentLocale={currentLocale}
          displayedComponent={displayedComponent}
          displayGroups={displayGroups}
          editOperations={editOperations}
          navigationOperations={navigationOperations}
          unitPath={unitPath}
        />
        <UnitsList
          course={course}
          currentLocale={currentLocale}
          displayedComponent={displayedComponent}
          displayGroups={displayGroups}
          editOperations={editOperations}
          navigationOperations={navigationOperations}
          rootUrl={rootUrl}
          unitPath={unitPath}
        />
      </ToggleDisplay>
    </ScrollableNav>
  );
};

export default CourseNavigation;
